import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ColumnDefinition, Empty } from 'barte-design-system';
import { usePostHog } from '@posthog/react';

import { Title, TableSkeleton } from '@/modules/shared/components'
import { ROUTES_PAYFAC, TRACK_EVENTS } from '@payfac/constants'
import { useGetRatePlansList } from '@payfac/services';

import styles from './list.module.css';

import type { IRatePlanList } from '@payfac/types';

export const RatePlanList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const posthog = usePostHog();

    const [page, setPage] = useState(1);

    const { data, isLoading } = useGetRatePlansList({
        filters: { page, limit: 10, webapp: true }
    });

    const ratePlans = data?.data?.data || [];

    const columns: ColumnDefinition<IRatePlanList>[] = [
        {
            key: 'id',
            title: t('payfac.pageRatePlansTableColumnId'),
            dataIndex: 'id'
        },
        {
            key: 'name',
            title: t('payfac.pageRatePlansTableColumnName'),
            dataIndex: 'name'
        },
        {
            key: 'totalSellers',
            title: t('payfac.pageRatePlansTableColumnTotalSellers'),
            dataIndex: 'totalSellers'
        },

    ];

    const handleNavigateToShow = (id: number) => {
        navigate(ROUTES_PAYFAC.RATE_PLANS.SHOW.fullPath(id));
    };

    const handleNavigateToNew = () => {
        navigate(ROUTES_PAYFAC.RATE_PLANS.FORM.fullPath);
    }

    const showEmptyState = !isLoading && (ratePlans?.length === 0 || data?.data?.pagination?.total === 0);

    useEffect(() => {
        posthog.capture(TRACK_EVENTS.RATE_PLAN.LIST_VIEWED);
    }, []);

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{t('payfac.pageRatePlansTitle')} - Barte</title>
            </Helmet>

            <div className={styles.header}>
                <Title variant='h5' as="h1">{t('payfac.pageRatePlansTitle')}</Title>
                <Button onClick={handleNavigateToNew}>{t('payfac.pageRatePlansCreatePlanButton')}</Button>
            </div>

            {showEmptyState && <Empty showButton={false} />}

            {!showEmptyState && <div className={styles.table}>
                <TableSkeleton<IRatePlanList>
                    isLoading={isLoading}
                    columns={columns}
                    data={ratePlans.map((item) => ({
                        ...item,
                        onClick: () => handleNavigateToShow(item.id),
                    }))}
                    rowKey={record => record.id}
                    pagination={{
                        currentPage: page,
                        totalPages: data?.data?.pagination?.pages || 1,
                        onPageChange: setPage
                    }}
                    onRowClick={(record) => navigate(ROUTES_PAYFAC.RATE_PLANS.SHOW.fullPath(record.id))}
                />
            </div>}
        </div>
    );
};
